import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'

// Superuser chat on the admin conversation page. Same server-rendered shape as
// public-conversation-controller: every signal (live broadcast, reconnect, own
// send) funnels into refresh(), which fetches the stream fragment bounded by
// after_id and appends only what's new. Differences from the public one: the
// shared cookie-auth consumer (so no consumer.disconnect() — other subscriptions
// may ride the same socket), and the conversation always exists (no lazy-creation
// dance). Sandbar is full page loads, so disconnect() on navigation is automatic;
// the connect/disconnect pairing also holds if Turbo Drive ever arrives.
export default class extends Controller {
  static targets = ['scroll']
  static values = {
    conversationId: String,
    messagesUrl: String,
  }

  connect() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'ConversationChannel', conversation_id: this.conversationIdValue },
      {
        connected: () => this.refresh(), // covers reconnect gaps too
        received: envelope => this.received(envelope),
      },
    )
    this.scrollToBottom()
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
  }

  received(envelope) {
    if (envelope.v !== 1) return
    if (envelope.event === 'message.created') this.refresh()
  }

  async refresh() {
    const url = new URL(this.messagesUrlValue, window.location.origin)
    if (this.lastMessageId) url.searchParams.set('after_id', this.lastMessageId)

    const response = await fetch(url, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    })
    if (!response.ok) return

    this.appendFragment(await response.text())
  }

  // double-submit guard: disabled while the request is in flight (re-enabled in
  // finally), so rapid Enters can't duplicate. This is ours to own — ujs
  // disable-with doesn't compose with a preventDefaulted fetch (it never
  // re-enables), so the form opts out of it.
  async send(event) {
    event.preventDefault()
    const form = event.target
    const button = form.querySelector('[type="submit"]')
    if (button?.disabled) return

    if (button) button.disabled = true
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      })
      if (!response.ok) return

      form.reset()
      await this.refresh()
    } finally {
      if (button) button.disabled = false
    }
  }

  // parse the fragment and append only unseen messages
  appendFragment(html) {
    const template = document.createElement('template')
    template.innerHTML = html
    const fragment = template.content.querySelector('.chat-stream')
    if (!fragment) return

    fragment.querySelectorAll('[data-message-id]').forEach(node => {
      if (this.stream.querySelector(`[data-message-id="${node.dataset.messageId}"]`)) return

      this.stream.appendChild(node)
    })
    this.scrollToBottom()
  }

  get stream() {
    return this.scrollTarget.querySelector('.chat-stream')
  }

  // UUIDv7 ids arrive in order, so the last DOM node is the catch-up cursor
  get lastMessageId() {
    const nodes = this.stream.querySelectorAll('[data-message-id]')
    return nodes.length ? nodes[nodes.length - 1].dataset.messageId : null
  }

  scrollToBottom() {
    this.scrollTarget.scrollTop = this.scrollTarget.scrollHeight
  }
}
